import { Button, Chip, CircularProgress, Grid, Stack, Tooltip, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import type { AnnualPlanOption } from '../../../models/requestObjects/AnnualPlanResponseObjects'
import { GetAnnualPlanOptions, GetContract } from '../../../services/AnnualPlanService'
import { FormatDateAsTimeAgo } from '../../../utils/dateUtils'
import { FileDownloadOutlined, OpenInNew } from '@mui/icons-material'
import { AnnualPlanStatus, ImplementationPhaseStatuses } from '../../../models/AnnualPlanStatus'
import { generatePath, useNavigate } from 'react-router'
import { AppRoutes } from '../../Routes'
import { ImplementationDetailsTabs } from './ImplementationDetails'
import { AnnualPlanTabs } from '../planning/PlanningDetails'
import { toast } from 'react-toastify'
import { GetFileDownloadUrl } from '../../../utils/fileUtils'

interface Props {
  rcnId: number
}
export const ImplementationAnnualPlanList = (props: Props): JSX.Element => {
  const [isLoading, setIsLoading] = useState(true)
  const [annualPlans, setAnnualPlans] = useState<AnnualPlanOption[]>([])
  const nav = useNavigate()
  const [isDownloadingContract, setIsDownloadingContract] = useState(false)

  useEffect(() => {
    if (props.rcnId == null) return

    const fetchData = async (): Promise<void> => {
      const plans = await GetAnnualPlanOptions(props.rcnId)
      setAnnualPlans(
        plans
          .filter(plan => ImplementationPhaseStatuses.includes(plan.status))
      )
      setIsLoading(false)
    }

    void fetchData()
  }, [props.rcnId])

  if (isLoading) return <CircularProgress />

  const StatusChip = ({ status }: { status: AnnualPlanStatus }): JSX.Element => {
    let label: string = status
    let backgroundColor: string | undefined
    let color: string | undefined

    switch (status) {
      case AnnualPlanStatus.Finalized:
        label = 'Contract Finalized'
        backgroundColor = 'var(--purple-500)'
        color = 'var(--white)'
        break
      case AnnualPlanStatus.ImplementationComplete:
        label = 'Implementation Complete'
        backgroundColor = 'var(--green-500)'
        color = 'var(--white)'
        break
    }

    return <Chip
      data-testid='statusChip'
      label={label}
      sx={{ backgroundColor, color, padding: '0.5em 2em' }}
    />
  }

  const handleActionButton = (annualPlanId: number): void => {
    const path = generatePath(
      AppRoutes.IMPLEMENTATION_DETAILS,
      {
        rcnId: props.rcnId.toString(),
        planId: annualPlanId.toString(),
        tab: ImplementationDetailsTabs.GettingStarted.toString()
      }
    )
    nav(path)
  }

  const navigateToContract = (annualPlanId: number): void => {
    const path = generatePath(
      AppRoutes.PLANNING_DETAILS,
      {
        rcnId: props.rcnId.toString(),
        planId: annualPlanId.toString(),
        tab: AnnualPlanTabs.GettingStarted.toString()
      }
    )
    nav(path)
  }

  const handleDownloadContract = async (annualPlanId: number): Promise<void> => {
    try {
      setIsDownloadingContract(true)
      const contract = await GetContract(annualPlanId)
      const contractKey = contract?.startSignedContractKey
      if (contractKey == null) {
        toast.error('No START signed contract file available for download.')
        console.debug('No contract file available for Annual Plan ID:', annualPlanId)
        return
      }

      const fileUrl = GetFileDownloadUrl(contractKey)
      if (fileUrl == null) {
        toast.error('Failed to download contract: Invalid file URL.')
        return
      }

      const link = document.createElement('a')
      link.href = fileUrl
      link.download = contractKey.split('/').pop() ?? 'contract.pdf'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      toast.success('Contract downloaded successfully.')
    } catch (error) {
      toast.error('Something went wrong while downloading the contract.')
      console.error('Download contract error:', error)
    } finally {
      setIsDownloadingContract(false)
    }
  }

  const actionButtonText = (status: AnnualPlanStatus): string => {
    switch (status) {
      case AnnualPlanStatus.Finalized:
        return 'Get Started'
      case AnnualPlanStatus.ImplementationComplete:
        return 'View'
      default:
        return 'Open'
    }
  }

  const dataTestId = 'implementationAnnualPlanList'

  if (annualPlans.length === 0) {
    return (
      <Typography variant='body1' data-testid={dataTestId}>
        No finalized annual plans available.
      </Typography>
    )
  }

  return (
    <>
      <Stack spacing='12px' data-testid={dataTestId}>
        {/* Header */}
        <Grid container spacing={2} flexWrap='nowrap' sx={{ padding: '1em 2em' }}>
          <Grid item xs={6}>
            <Typography variant='h4'>School Year</Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant='h4'>Status</Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant='h4'>Actions</Typography>
          </Grid>
        </Grid>

        {/* Annual Plan List */}
        {annualPlans.map(annualPlan => (
          <Grid
            container
            flexWrap='nowrap'
            data-testid='implementationAnnualPlan'
            spacing={2}
            key={`${annualPlan.id}-${annualPlan.name}`}
            sx={{
              padding: '1em 2em',
              border: '1px solid var(--grey-100)',
              boxShadow: '0px 10px 10px -8px rgba(59, 53, 62, 0.3)',
              borderRadius: '1em',
              alignItems: 'center',
              overflow: 'hidden'
            }}
          >
            <Grid item xs={6}>
              <Typography variant='h4'>
                {annualPlan.name.replace('Annual Plan', '').trim()}
              </Typography>
              {annualPlan.createDate != null && (
                <Typography variant='body1' color='#808394'>{FormatDateAsTimeAgo(new Date(annualPlan.createDate))}</Typography>
              )}
            </Grid>
            <Grid item xs={3}>
              <StatusChip status={annualPlan.status} />
            </Grid>
            <Grid item xs={3}>
              <Stack direction='row' spacing='30px'>
                <Button
                  variant='outlined'
                  size='large'
                  data-testid='actionButton'
                  sx={{ color: 'var(--grey-700)', borderColor: 'var(--grey-100)', minWidth: '140px' }}
                  onClick={() => { handleActionButton(annualPlan.id) }}
                >
                  {actionButtonText(annualPlan.status)}
                </Button>

                <Tooltip title='Download Contract Data'>
                  <Button
                    variant='outlined'
                    size='large'
                    data-testid='downloadContractButton'
                    sx={{
                      color: 'var(--grey-700)',
                      borderColor: 'var(--grey-100)',
                      minWidth: 'unset',
                      padding: '8px'
                    }}
                    onClick={() => { void handleDownloadContract(annualPlan.id) }}
                    disabled={isDownloadingContract}
                  >
                    {isDownloadingContract
                      ? <CircularProgress size={24} />
                      : <FileDownloadOutlined />}
                  </Button>
                </Tooltip>

                <Tooltip title='View Contract Data'>
                  <Button
                    variant='outlined'
                    size='large'
                    data-testid='viewContractButton'
                    sx={{
                      color: 'var(--grey-700)',
                      borderColor: 'var(--grey-100)',
                      minWidth: 'unset',
                      padding: '8px'
                    }}
                    onClick={() => { navigateToContract(annualPlan.id) }}
                  >
                    <OpenInNew />
                  </Button>
                </Tooltip>
              </Stack>
            </Grid>
          </Grid>
        ))}
      </Stack>
    </>
  )
}
